import { useMutation, useQueryClient } from "@tanstack/react-query";
import { userInfoKeys } from "Auth/Api/Queries/useUserInfoQuery";
import {
  type SetStardustParticipationRequest,
  type SetStardustParticipationResult,
  putLoyaltiesStardustParticipation,
} from "Infrastructure/Api/Api";
import { processResponseWithApiProblemDetails } from "Infrastructure/Api/ApiUtils";

type Props = {
  onSuccess?: (result: SetStardustParticipationResult) => void;
  onError?: (error: unknown) => void;
};

/**
 * Records the person's Stardust consent, refusal or leaving (ZS-1624). Every reader of the state
 * (consent dialog, Stardust page and section, header points, daily-open hook) uses the React Query
 * user-info, so a successful save refetches it — the UI flips with the fresh state, never with an
 * optimistic guess. The caller's onSuccess runs BEFORE the refetch so a caller that leaves the page
 * (the section after leaving the program) is gone before the page would re-render into the consent
 * dialog; the mutation stays pending until the refetch is done.
 */
export const useSetStardustParticipationMutation = ({
  onSuccess,
  onError,
}: Props = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: processResponseWithApiProblemDetails(
      (data: SetStardustParticipationRequest) =>
        putLoyaltiesStardustParticipation(data),
    ),
    onSuccess: async (result: SetStardustParticipationResult) => {
      onSuccess?.(result);
      await queryClient.invalidateQueries({ queryKey: userInfoKeys.all });
    },
    onError,
  });
};
